import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "frontrow.nativePush.token";
let registration = null;
let active = false;
let navigationListener = null;

export function isNativePushSupported() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

// Synchronous on purpose: resolving a promise with this native proxy makes JS
// call PushNotifications.then(), which the iOS bridge rejects.
function plugin() {
  return PushNotifications;
}

function savedToken() {
  try { return window.localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export async function initialiseNativePushNavigation() {
  if (!isNativePushSupported()) return;
  if (!navigationListener) {
    navigationListener = (async () => {
      const push = plugin();
      return push.addListener("pushNotificationActionPerformed", () => {
        // Never use a URL from a notification payload. Auth still guards the dashboard.
        window.location.assign("/");
      });
    })();
    navigationListener.catch(() => { navigationListener = null; });
  }
  await navigationListener;
}

export async function readNativeNotificationState(enabled) {
  try {
    const permission = (await plugin().checkPermissions()).receive;
    if (permission === "denied") return "denied";
    return enabled && permission === "granted" && active ? "on" : "off";
  } catch { return "unsupported"; }
}

async function registerDevice(push) {
  if (registration) return registration;
  registration = (async () => {
    let successListener, errorListener, timer;
    try {
      return await new Promise((resolve) => {
        let settled = false;
        const finish = (result) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(result);
        };
        timer = setTimeout(() => finish({ status: "error" }), 30000);
        (async () => {
          successListener = await push.addListener("registration", async ({ value }) => {
            if (settled) return;
            // Resolve only after the authenticated backend has accepted this device.
            try {
              window.localStorage.setItem(TOKEN_KEY, value);
              await axios.post(`${API}/push/native/subscribe`, { device_token: value }, { timeout: 10000 });
              if (!settled) active = true;
              finish({ status: "granted" });
            } catch { finish({ status: "error" }); }
          });
          errorListener = await push.addListener("registrationError", () => finish({ status: "error" }));
          await push.register();
        })().catch(() => finish({ status: "error" }));
      });
    } finally {
      clearTimeout(timer);
      await successListener?.remove();
      await errorListener?.remove();
      registration = null;
    }
  })();
  return registration;
}

export async function enableNativeNotifications(prompt = false) {
  try {
    const config = await axios.get(`${API}/push/config`);
    if (!config.data?.native_enabled) return { status: "not_configured" };
    await initialiseNativePushNavigation();
    const push = plugin();
    let permission = (await push.checkPermissions()).receive;
    if (permission !== "granted" && prompt) permission = (await push.requestPermissions()).receive;
    if (permission !== "granted") return { status: permission === "denied" ? "denied" : "permission_required" };
    return await registerDevice(push);
  } catch { return { status: "error" }; }
}

export async function detachNativeNotifications() {
  if (!isNativePushSupported()) return;
  // Complete registration before deleting it, using the current account's auth.
  if (registration) await registration;
  const token = savedToken();
  if (token) await axios.post(`${API}/push/native/unsubscribe`, { device_token: token }, { timeout: 10000 });
  await plugin().unregister();
  active = false;
  try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* no persistent token */ }
}
